import { getSizeValue, ProductAttribute, validateCustomSize } from "@wttb/product-configurator";
import cn from "clsx";
import { useTranslations } from "next-intl";
import { useCallback, useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";

import { Input } from "@/common/components/form/input";
import { Link } from "@/common/components/link";
import { debounce } from "@/common/utils/debounce";
import { useStoreProduct } from "@/features/product/contexts/product-context";
import Paper from "./paper";

enum OrientationValue {
  Landscape = "landscape",
  Portrait = "portrait",
  Square = "square",
}

const useSizeMutation = () => {
  const { setCustomConfigValue, setConfigValue, attributes } = useStoreProduct();
  // We need to change orientation based on custom width and height
  const attribute = useMemo(
    () => (attributes as ProductAttribute[]).find((attr) => attr.key === "orientation"),
    [attributes],
  );

  const orientationValues = useMemo(() => {
    if (!attribute) return null;
    const landscape = attribute.values.find(
      (attrVal) => attrVal.value === OrientationValue.Landscape,
    );
    const portrait = attribute.values.find(
      (attrVal) => attrVal.value === OrientationValue.Portrait,
    );
    const square = attribute.values.find((attrVal) => attrVal.value === OrientationValue.Square);

    if (!landscape || !portrait || !square) return null;

    return {
      landscape: landscape.id,
      portrait: portrait.id,
      square: square.id,
    };
  }, [attribute]);

  // Set Custom size and orientation changes to product config
  const setSize = (width: number, height: number, isValid = true) => {
    if (!isValid) return setCustomConfigValue("size", null);
    if (orientationValues) {
      const orientationId =
        width > height
          ? orientationValues[OrientationValue.Landscape]
          : width < height
            ? orientationValues[OrientationValue.Portrait]
            : orientationValues[OrientationValue.Square];
      setConfigValue("orientation", orientationId);
    }
    setCustomConfigValue("size", getSizeValue(width, height));
  };

  return setSize;
};

type SizeCustomFormProps = {
  attribute: ProductAttribute;
  className?: string;
};

function SizeCustomForm({ attribute, className }: SizeCustomFormProps) {
  const { selectedOptions, pricingResult } = useStoreProduct();
  const t = useTranslations("custom-attributes-forms");
  const settings = useMemo(
    () => ({
      minW: +attribute.minWidth!,
      maxW: +attribute.maxWidth!,
      minH: +attribute.minHeight!,
      maxH: +attribute.maxHeight!,
    }),
    [attribute],
  );
  const initialSizes = useMemo(() => {
    if (selectedOptions?.size && /^\d+x\d+$/.test(selectedOptions.size)) {
      const sizes = selectedOptions.size.split("x").map(Number);
      return {
        width: sizes[0],
        height: sizes[1],
      };
    }
    return {
      width: settings.minW,
      height: settings.minH,
    };
  }, []);
  const [orientation, setOrientation] = useState({
    isSquare: initialSizes.width === initialSizes.height,
    isLandscape: initialSizes.width >= initialSizes.height,
  });
  const {
    register,
    getValues,
    formState: { errors },
  } = useForm<{
    width: number;
    height: number;
  }>({
    mode: "onChange",
    defaultValues: { width: initialSizes.width, height: initialSizes.height },
  });

  const setSize = useSizeMutation();
  const debouncedOnChange = useCallback(debounce(setSize, 1500), []);
  const validateSize = useCallback(validateCustomSize, [settings]);

  useEffect(() => {
    setSize(initialSizes.width, initialSizes.height);
  }, []);

  const handleChange = () => {
    const [w, h] = getValues(["width", "height"]);
    const isValid = validateSize(w, h, settings);
    setOrientation({ isSquare: w === h, isLandscape: w >= h });
    debouncedOnChange(w, h, isValid);
  };

  return (
    <>
      <div className={cn("flex flex-col items-start sm:flex-row", className)}>
        <form noValidate className="mr-[75px] flex flex-col justify-center text-left">
          <p className="!text-grey-90 text-left text-base !leading-7 font-semibold">
            {["book", "flat", "wide"].includes(pricingResult?.details?.calculator as string)
              ? t("text-finished-size")
              : t("text-flat-size-before-folding")}
          </p>
          <Input
            label={t("label", {
              value: t("text-height"),
              max: settings.maxH,
              min: settings.minH,
            })}
            variant="solid"
            type="number"
            {...register("height", {
              valueAsNumber: true,
              required: t("error-required"),
              onChange: handleChange,
              validate: {
                isPositive: (v) => Number(v) >= 0 || t("error-positive"),
                max: (v) => Number(v) <= settings.maxH || t("error-max"),
                min: (v) => Number(v) >= settings.minH || t("error-min"),
              },
            })}
            error={errors.height && errors.height.message!}
            postfixNode={<span className="absolute right-[-35px]">{t("text-mm")}</span>}
            className="mt-3 w-44"
            inputClassName="h-12"
            labelClassName="!text-sm !leading-none !text-grey-90 !font-normal text-left whitespace-pre-line"
          />

          <Input
            label={t("label", {
              value: t("text-width"),
              max: settings.maxW,
              min: settings.minW,
            })}
            type="number"
            variant="solid"
            {...register("width", {
              valueAsNumber: true,
              required: t("error-required"),
              onChange: handleChange,
              validate: {
                isPositive: (v) => Number(v) >= 0 || t("error-positive"),
                max: (v) => Number(v) <= settings.maxW || t("error-max"),
                min: (v) => Number(v) >= settings.minW || t("error-min"),
              },
            })}
            error={errors.width && errors.width.message!}
            postfixNode={<span className="absolute right-[-35px]">{t("text-mm")}</span>}
            className="mt-3 w-44"
            inputClassName="h-12"
            labelClassName="!text-sm !leading-none !text-grey-90 !font-normal text-left whitespace-pre-line"
          />
        </form>

        <div className="mt-4 flex flex-col sm:mt-12">
          <p className="!text-grey-90 text-left text-base !leading-7">{t("text-orientation")}</p>

          <div className="flex items-center">
            <Paper
              className="mr-3"
              isSquare={orientation.isSquare}
              active={!orientation.isLandscape || orientation.isSquare}
            />
            {!orientation.isSquare && <Paper isLandscape active={orientation.isLandscape} />}
          </div>
        </div>
      </div>
      <Link href="/" className={cn("text-grey-90 text-base", className)}>
        {t("text-link")}
      </Link>
    </>
  );
}

export { SizeCustomForm };
